import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { CharacteristicImage, EvaluationService, Status, translateCommonErrorStatus } from '@core/data';
import { PendingUploadModel } from '../images-form/images-form.state';
import { forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { FormControl, FormGroup } from '@angular/forms';
import { coerceMomentProperty } from '@core/utils';

// Shared by the gallery grid and the viewer dialog - both edit the same CharacteristicImage.takenAt field.
export function makeCharacteristicImageDateGroup(image: CharacteristicImage): FormGroup {
    return new FormGroup({
        key: new FormControl(image.key),
        takenAt: new FormControl(coerceMomentProperty(image.takenAt)),
    });
}

export interface PendingCharacteristicImageUpload extends Omit<PendingUploadModel, 'data'> {
    data: CharacteristicImage;
}

export interface CharacteristicImageGalleryStateModel {
    evalKey: string;
    protocolId: string;
    status: Status;
    saveStatus: Status;
    images: CharacteristicImage[];
    uploads: PendingCharacteristicImageUpload[];
    pendingDeleteKeys: string[];
}

export class InitCharacteristicImageGallery {
    static readonly type = "[CharacteristicImageGallery] Init";
    constructor(public evalKey: string, public protocolId: string){}
}

export class UploadCharacteristicImage {
    static readonly type = "[CharacteristicImageGallery] Upload";
    constructor(public file: File, public charId: string){}
}

export class ToggleCharacteristicImageDelete {
    static readonly type = "[CharacteristicImageGallery] ToggleDelete";
    constructor(public key: string){}
}

export class SaveCharacteristicImageGallery {
    static readonly type = "[CharacteristicImageGallery] Save";
    constructor(public dateUpdates: Array<{ key: string; takenAt: string }> = []){}
}

// Replaces a single image in state by key - keeps the store in sync after the viewer saves a field directly against the API.
export class PatchCharacteristicImage {
    static readonly type = "[CharacteristicImageGallery] Patch";
    constructor(public image: CharacteristicImage){}
}

@State<CharacteristicImageGalleryStateModel>({
    name: "characteristic_image_gallery",
    defaults: {
        evalKey: null,
        protocolId: null,
        status: Status.UNINITIALIZED,
        saveStatus: Status.OK,
        images: [],
        uploads: [],
        pendingDeleteKeys: []
    }
})
@Injectable()
export class CharacteristicImageGalleryState {

    @Selector()
    static images(state: CharacteristicImageGalleryStateModel){
        return state.images;
    }

    @Selector()
    static status(state: CharacteristicImageGalleryStateModel){
        return state.status;
    }

    @Selector()
    static saveStatus(state: CharacteristicImageGalleryStateModel){
        return state.saveStatus;
    }

    @Selector()
    static uploads(state: CharacteristicImageGalleryStateModel){
        return state.uploads;
    }

    @Selector()
    static pendingDeleteKeys(state: CharacteristicImageGalleryStateModel){
        return state.pendingDeleteKeys;
    }

    constructor(
        private _evalService: EvaluationService,
    ){}

    @Action(InitCharacteristicImageGallery)
    initCharacteristicImageGallery(ctx: StateContext<CharacteristicImageGalleryStateModel>, action: InitCharacteristicImageGallery){

        ctx.setState({
            evalKey: action.evalKey,
            protocolId: action.protocolId,
            status: Status.LOADING,
            saveStatus: Status.OK,
            images: [],
            uploads: [],
            pendingDeleteKeys: []
        });

        return this._evalService
            .getCharacteristicImages(action.evalKey)
            .pipe(tap(
                result => {
                    ctx.patchState({
                        images: result,
                        status: Status.OK
                    });
                },
                error => {
                    ctx.patchState({
                        status: translateCommonErrorStatus(error)
                    });
                }
            ));
    }

    @Action(UploadCharacteristicImage)
    uploadCharacteristicImage(ctx: StateContext<CharacteristicImageGalleryStateModel>, action: UploadCharacteristicImage){

        const state = ctx.getState();
        const evalKey = state.evalKey;

        let upload: PendingCharacteristicImageUpload = {
            id: Math.round(Math.random() * 10000000),
            loaded: 0,
            total: action.file.size,
            status: Status.LOADING,
            name: action.file.name,
            data: null
        };

        ctx.patchState({
            uploads: [...state.uploads, upload]
        });

        // guard every callback against the gallery having moved on to a different
        // evaluation (dialog closed/reopened) while this upload was still in flight
        return this._evalService.uploadCharacteristicImage(evalKey, action.charId, action.file)
            .pipe(tap(
                event => {

                    if(ctx.getState().evalKey !== evalKey) return;

                    if(event.type === HttpEventType.UploadProgress){

                        upload = {
                            ...upload,
                            loaded: event.loaded,
                            total: event.total,
                        };

                        this.patchUpload(ctx, upload);

                    }else if(event instanceof HttpResponse){

                        upload = {
                            ...upload,
                            status: Status.COMPLETE,
                            data: event.body
                        };
                        this.patchUpload(ctx, upload);

                        const current = ctx.getState();
                        ctx.patchState({
                            images: [...current.images, event.body as CharacteristicImage]
                        });

                    }

                },
                error => {

                    if(ctx.getState().evalKey !== evalKey) return;

                    upload = {
                        ...upload,
                        status: translateCommonErrorStatus(error)
                    };

                    this.patchUpload(ctx, upload);

                }
            ), catchError(e => of(null)));

    }

    @Action(ToggleCharacteristicImageDelete)
    toggleCharacteristicImageDelete(ctx: StateContext<CharacteristicImageGalleryStateModel>, action: ToggleCharacteristicImageDelete){

        const state = ctx.getState();

        const pendingDeleteKeys = state.pendingDeleteKeys.includes(action.key)
            ? state.pendingDeleteKeys.filter(key => key !== action.key)
            : [...state.pendingDeleteKeys, action.key];

        ctx.patchState({ pendingDeleteKeys });

    }

    @Action(PatchCharacteristicImage)
    patchCharacteristicImage(ctx: StateContext<CharacteristicImageGalleryStateModel>, action: PatchCharacteristicImage){

        const state = ctx.getState();

        ctx.patchState({
            images: state.images.map(image => image.key === action.image.key ? action.image : image)
        });

    }

    @Action(SaveCharacteristicImageGallery)
    saveCharacteristicImageGallery(ctx: StateContext<CharacteristicImageGalleryStateModel>, action: SaveCharacteristicImageGallery){

        const state = ctx.getState();

        if(state.pendingDeleteKeys.length === 0 && action.dateUpdates.length === 0){
            ctx.patchState({ saveStatus: Status.COMPLETE });
            return;
        }

        ctx.patchState({ saveStatus: Status.LOADING });

        // resolve every delete/date-update independently (not a bare forkJoin) so one
        // failing item can't discard the items that were already saved successfully
        const deleteCalls = state.pendingDeleteKeys.map(key => this._evalService.deleteCharacteristicImage(key)
            .pipe(
                map(() => ({ kind: 'delete' as const, key, success: true, image: null as CharacteristicImage })),
                catchError(() => of({ kind: 'delete' as const, key, success: false, image: null as CharacteristicImage }))
            )
        );

        const dateCalls = action.dateUpdates.map(update => this._evalService.updateCharacteristicImage(update.key, update.takenAt)
            .pipe(
                map(image => ({ kind: 'date' as const, key: update.key, success: true, image })),
                catchError(() => of({ kind: 'date' as const, key: update.key, success: false, image: null as CharacteristicImage }))
            )
        );

        return forkJoin([...deleteCalls, ...dateCalls]).pipe(tap(
            results => {

                const succeededDeleteKeys = results.filter(r => r.kind === 'delete' && r.success).map(r => r.key);
                const failedDeleteKeys = results.filter(r => r.kind === 'delete' && !r.success).map(r => r.key);
                const succeededDates = results.filter(r => r.kind === 'date' && r.success);
                const anyDateFailed = results.some(r => r.kind === 'date' && !r.success);

                const current = ctx.getState();

                let images = current.images.filter(image => !succeededDeleteKeys.includes(image.key));
                images = images.map(image => {
                    const match = succeededDates.find(r => r.key === image.key);
                    return match ? match.image : image;
                });

                ctx.patchState({
                    images,
                    pendingDeleteKeys: current.pendingDeleteKeys.filter(key => failedDeleteKeys.includes(key)),
                    saveStatus: (failedDeleteKeys.length === 0 && !anyDateFailed) ? Status.COMPLETE : Status.ERROR
                });

            }
        ));

    }

    private patchUpload(ctx: StateContext<CharacteristicImageGalleryStateModel>, upload: PendingCharacteristicImageUpload){

        const state = ctx.getState();

        let uploads = state.uploads.map(existing => {
            if(existing.id === upload.id){
                return upload;
            }
            return existing;
        });

        ctx.patchState({ uploads });

    }

}
